/**
 * Format a byte count for display next to a file name.
 *
 * Uses decimal units (kB/MB/GB, 1000-based), not binary ones, because this is
 * consumer-facing file-size text and every OS file browser the user has seen
 * reports decimal. The transfer engine's own limits are binary (MiB parts, a
 * 4 GiB ceiling); those are internal and are not what this renders.
 *
 * `locale` is threaded through so the decimal separator follows the user's
 * language — "1,5 MB" in de, "1.5 MB" in en. The unit suffix itself is not
 * localised: kB/MB/GB are used unchanged across the locales this app ships.
 */
const UNITS = ["B", "kB", "MB", "GB", "TB"] as const;

export function formatBytes(bytes: number, locale?: string): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1000) return `${Math.round(bytes)} B`;

  let value = bytes;
  let unit = 0;
  // Stop at TB rather than running off the end of UNITS.
  while (value >= 1000 && unit < UNITS.length - 1) {
    value /= 1000;
    unit += 1;
  }

  // One decimal below 10 (1.4 MB reads better than 1 MB); none above, where
  // the extra digit is noise.
  const digits = value < 10 ? 1 : 0;
  return `${value.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} ${UNITS[unit]}`;
}
